<template>
    <div>
        <OrderProductAttachmentDialog
            :is-open="orderProductAttachmentDialog"
            :order-product-data="orderProduct"
            @on-save="saveOrderProductAttachment"
            @on-delete="deleteOrderProductAttachment"
            @on-close="closeOrderProductAttachmentDialog"
        />
        <JobNotesDialog
            :is-open="jobNotesDialog"
            :job-id="jobData.id"
            @on-close="() => jobNotesDialog = false"
        />
        <ProductAmountDialog
            :is-open="productAmountDialog"
            :order-product-data="selectedOrderProduct"
            @on-save="saveOrderProductAmount"
            @on-close="closeProductAmountDialog"
        />
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="900px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Parametry zlecenia
                    </h1>
                    <v-spacer />
                    <v-btn
                        data-sentry-label="Uwagi jakościowe"
                        color="secondary"
                        class="mx-2"
                        outlined
                        @click="() => jobNotesDialog = true"
                    >
                        Uwagi jakościowe
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-container fluid class="px-6 py-2">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="job.name"
                                    label="Nazwa zlecenia"
                                    outlined
                                    dense
                                    :readonly="!isJobEditable"
                                    :rules="validRuleName"
                                />
                                <v-textarea
                                    v-model="job.description"
                                    label="Uwagi do zlecenia"
                                    rows="12"
                                    row-height="15"
                                    clearable
                                    outlined
                                    dense
                                    :readonly="!isJobEditable"
                                />
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-divider />

                    <v-container fluid class="px-6 py-2">
                        <v-row>
                            <v-col cols="12">
                                <v-data-table
                                    :headers="headers"
                                    :items="job.orderProduct"
                                    :mobile-breakpoint="0"
                                    hide-default-footer
                                    disable-pagination
                                >
                                    <template #item.name="{item}">
                                        <v-edit-dialog
                                            v-if="isJobEditable"
                                            :return-value="item.name"
                                            large
                                            lazy
                                            cancel-text="Anuluj"
                                            save-text="Zapisz"
                                            persistent
                                            @open="openOrderProductName(item)"
                                            @save="saveOrderProductName(item)"
                                            @cancel="cancelOrderProductName"
                                            @close="closeOrderProductName"
                                        >
                                            <div class="d-inline-flex">
                                                {{ item.name }}
                                                <v-icon color="primary" small class="ml-1">
                                                    mdi-pencil
                                                </v-icon>
                                            </div>
                                            <template #input>
                                                <v-text-field
                                                    v-model="item.name"
                                                    label="Nazwa"
                                                    required
                                                    single-line
                                                />
                                            </template>
                                        </v-edit-dialog>
                                        <template v-else>
                                            {{ item.name }}
                                        </template>
                                    </template>
                                    <template #item.productType="{item}">
                                        {{ item.productType.name }}
                                    </template>
                                    <template #item.amount="{item}">
                                        <div>
                                            {{ item.amount + item.extraAmount }}
                                            <v-btn
                                                v-if="isJobEditable"
                                                icon
                                                small
                                                class="ml-1"
                                                @click="openProductAmountDialog(item)"
                                            >
                                                <v-icon color="primary" small>
                                                    mdi-pencil
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <template #item.plannedSendDate="{item}">
                                        {{ formatDate(item.plannedSendDate) }}
                                    </template>
                                    <template #item.receiveFileDate="{item}">
                                        {{ formatDate(item.receiveFileDate, false) }}
                                    </template>
                                    <template #item.actions="{item}">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Otwórz"
                                                        v-on="on"
                                                        @click="showOrderProductAttachmentDialog(item)"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Edytuj</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <template v-if="isJobEditable">
                        <v-btn
                            color="error"
                            text
                            data-sentry-label="Anuluj"
                            @click="onClose"
                        >
                            Anuluj
                        </v-btn>
                        <v-spacer />
                        <v-btn
                            color="success"
                            text
                            data-sentry-label="Zapisz"
                            @click="saveJob"
                        >
                            Zapisz
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn
                            color="error"
                            text
                            data-sentry-label="Zamknij"
                            @click="onClose"
                        >
                            Zamknij
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'
import { required } from 'vuelidate/lib/validators'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import { mask } from 'vue-the-mask'
import { validRuleIllegalCharactersJobName, validRuleNameLength } from '@/utils/customValidators'

export default {
    name: 'JobParametersDialog',
    components: {
        OrderProductAttachmentDialog: () => import('./OrderProductAttachmentDialog.vue'),
        JobNotesDialog: () => import('./JobNotesDialog.vue'),
        ProductAmountDialog: () => import('./ProductAmountDialog.vue')
    },
    directives: { mask },
    mixins: [ dateFormatter, tooltipDelay ],
    props: {
        isOpen: {
            type: Boolean
        },
        jobData: {
            type: Object,
            default: null
        },
        isJobEditable: {
            type: Boolean
        }
    },
    data() {
        return {
            job: {
                description: null,
                name: null
            },
            orderProduct: {
                orderProductAttachment: []
            },
            jobNotesDialog: false,
            currentOrderProductsNames: [],
            currentOrderProduct: null,
            orderProductAttachmentDialog: false,
            productAmountDialog: false,
            selectedOrderProduct: null,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false,
                    width: 220,
                },
                {
                    text: 'Rodzaj produktu',
                    value: 'productType',
                    sortable: false
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    width: 120,
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Data wysyłki',
                    value: 'plannedSendDate',
                    align: 'left',
                    sortable: false,
                    width: 170,
                },
                {
                    text: 'Data dostarczenia plików',
                    value: 'receiveFileDate',
                    align: 'left',
                    sortable: false,
                    width: 140,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
            validRuleName: [
                value => validRuleNameLength(value) || 'Nazwa nie może być dłuższa niż 80 znaków',
                value => !validRuleIllegalCharactersJobName(value) || 'Niedozwolone znaki –  /:\'"?*<>|()[]$#@{}~^&'
            ],
        }
    },
    validations() {
        return {
            job: {
                orderProduct: {
                    $each: {
                        amount: {
                            required
                        }
                    }
                },
                name: {
                    required,
                    isNameValid: value => 
                        validRuleNameLength(value) &&
                        !validRuleIllegalCharactersJobName(value)
                }
            }
        }
    },
    watch: {
        jobData: {
            handler: function(newValue) {
                this.job = JSON.parse(JSON.stringify(newValue))
            },
            deep: true
        }
    },
    methods: {
        openOrderProductName(orderProduct) {
            this.currentOrderProduct = JSON.parse(JSON.stringify(orderProduct))
            this.currentOrderProductsNames = this.job.orderProduct.map(item => item.name)
        },
        cancelOrderProductName() {
            const index = this.job.orderProduct.findIndex(item => item.id === this.currentOrderProduct.id)

            this.job.orderProduct.splice(index, 1, this.currentOrderProduct)
        },
        saveOrderProductName(orderProduct) {
            const previousOrderProductName = this.currentOrderProduct.name,
                trimmedValue = orderProduct.name.trimStart().trimEnd(),
                isUnique = trimmedValue === previousOrderProductName || !this.currentOrderProductsNames.some(item => item === trimmedValue),
                index = this.job.orderProduct.findIndex(item => item.id === this.currentOrderProduct.id)

            if (!trimmedValue) { // Prevent empty value
                this.job.orderProduct[index].name = previousOrderProductName
            }

            if (isUnique) {
                this.job.orderProduct[index].name = orderProduct.name
            } else {
                this.job.orderProduct[index].name = previousOrderProductName

                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Produkt o podanej nazwie już istnieje.'
                })
            }
        },
        closeOrderProductName() {
            this.currentOrderProduct = null
            this.currentOrderProductsNames = []
        },
        saveJob() {
            this.$v.$touch()

            if (this.$v.job.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            if (this.$v.job.orderProduct.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę uzupełnić nakład.'
                })

                return
            }

            const promises = this.job.orderProduct.map(item => {
                return this.$store.dispatch('orders/updateOrderProduct', {
                    id: item.id,
                    amount: item.amount,
                    name: item.name
                })
            })

            this.$store.dispatch('toggleCircularLoader', true)

            Promise.all(promises)
                .then(() => {
                    this.$store.dispatch('jobs/saveJob', {
                        id: this.job.id,
                        description: this.job.description,
                        name: this.job.name,
                    })
                        .then(response => {
                            this.$emit('on-save', response.data)
                            this.onClose()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.$store.dispatch('toggleCircularLoader', false)
                        })
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })

        },
        async getOrderProduct(id) {
            return await this.$store.dispatch('orders/getOrderProduct', id)
                .then(response => {
                    this.orderProduct = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showOrderProductAttachmentDialog(id) {
            this.getOrderProduct(id)
                .then(() => {
                    this.orderProductAttachmentDialog = true
                })
        },
        closeOrderProductAttachmentDialog() {
            this.orderProductAttachmentDialog = false
            this.orderProduct = null

        },
        saveOrderProductAttachment(orderProductAttachment) {
            const index = this.job.orderProduct.findIndex(item => item.id == this.orderProduct.id)

            this.job.orderProduct[index].orderProductAttachment.push(orderProductAttachment)
        },
        deleteOrderProductAttachment(orderProductAttachmentIndex) {
            const index = this.job.orderProduct.findIndex(item => item.id == this.orderProduct.id)

            this.job.orderProduct[index].orderProductAttachment.splice(orderProductAttachmentIndex, 1)
        },
        onClose() {
            setTimeout(() => {
                this.job = JSON.parse(JSON.stringify(this.jobData))
            }, 500)

            this.$emit('on-close')
        },
        openProductAmountDialog(orderProduct) {
            this.selectedOrderProduct = JSON.parse(JSON.stringify(orderProduct))
            this.productAmountDialog = true
        },
        closeProductAmountDialog() {
            this.productAmountDialog = false
            this.selectedOrderProduct = null
        },
        saveOrderProductAmount(updatedOrderProduct) {
            const index = this.job.orderProduct.findIndex(item => item.id === updatedOrderProduct.id)
            
            if (index !== -1) {
                this.job.orderProduct[index].amount = updatedOrderProduct.amount
            }
            this.closeProductAmountDialog()
        },
    }
}
</script>
